/* eslint-disable no-param-reassign */
/* eslint-disable no-console */
import { React } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import i18n from "i18next";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useTranslation } from "react-i18next";
import { Field, reduxForm, formValueSelector } from "redux-form";

import FormField from "components/FormField";
import FormCheckbox from "components/FormCheckbox";
import FormSelectorMulti from "components/FormSelectorMulti";

import { newAlertMessage } from "store/actions/globalAlertActions";
import {
  sendAddProjectManager,
  sendUpdateProjectManager,
} from "store/actions/projectManagersActions";

const validate = (values) => {
  const errors = {};

  const required = ["firstName", "lastName", "email"];

  required.forEach((f) => {
    if (values[f] === undefined || values[f] === null || values[f] === "") {
      errors[f] = i18n.t("Required");
    }
  });

  if (values.email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)) {
    errors.email = "Invalid email address";
  }

  return errors;
};

function ProjectManagerForm(props) {
  const {
    handleSubmit,
    actionType,
    projectsOptions,
    actNewAlertMessage,
    actAddProjectManager,
    actUpdateProjectManager,
    onSuccessClose,
    projectManagerUid,
    isProjectMenuEnabled,
    isSettingsEnabled,
    isAnalyticsEnabled,
  } = props;

  const { t } = useTranslation();

  const onSubmitForm = async (values) => {
    if (actionType === "add" || actionType === "edit") {
      const projects = values.projects || [];
      const hasProjectMenuAccess = !!values?.features?.configuration;
      const hasSettingsAccess = hasProjectMenuAccess && !!values?.features?.settings;
      const hasAnalyticsAccess = !!values?.features?.analytics;
      const projectsToSend = projects.reduce((acc, projectUid) => {
        acc[projectUid] = true;
        return acc;
      }, {});
      try {
        const projectManagerData = {
          uid: projectManagerUid || values.uid || undefined,
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phoneNumber: values.phoneNumber,
          projects: projectsToSend,
          features: {
            amenities: !!values?.features?.amenities,
            analytics: !!values?.features?.analytics,
            chat: !!values?.features?.chat,
            services: !!values?.features?.services,
            support: !!values?.features?.support,
            clients: !!values?.features?.clients,
            apartments: !!values?.features?.apartments,
            notifications: !!values?.features?.notifications,
            configuration: hasProjectMenuAccess,
            settings: hasSettingsAccess,
            email: hasSettingsAccess && !!values?.features?.email,
            devices: hasProjectMenuAccess && !!values?.features?.devices,
            occupancy: hasProjectMenuAccess && !!values?.features?.occupancy,
            cloudApis: hasProjectMenuAccess && !!values?.features?.cloudApis,
            customApp: hasSettingsAccess && !!values?.features?.customApp,
            metricsConfig:
              hasSettingsAccess && hasAnalyticsAccess && !!values?.features?.metricsConfig,
            // certifications: !!values?.features?.certifications,
          },
        };

        if (actionType === "add") {
          await actAddProjectManager(projectManagerData);
        } else {
          await actUpdateProjectManager(projectManagerData);
        }

        if (onSuccessClose) onSuccessClose();
      } catch (error) {
        console.error("Error saving project manager:", error);

        const errorCode = error?.error || error?.message || "";
        const errorContent =
          errorCode === "userIsMainUser" ? "projectManagerAddMainUserError" : "unknownError";

        actNewAlertMessage({
          type: "error",
          title: "Project Managers",
          content: errorContent,
        });
      }
    }
  };

  return (
    <MDBox py={3}>
      <form onSubmit={handleSubmit(onSubmitForm)}>
        <Grid container justifyContent="center">
          <Card id="basic-info">
            <MDBox
              height="4rem"
              bgColor="info"
              variant="gradient"
              coloredShadow="info"
              borderRadius="xl"
              display="flex"
              justifyContent="center"
              alignItems="center"
              color="white"
              mt={-3}
              mx={10}
              px={2}
            >
              {t(actionType)} {t("projectManager")}
            </MDBox>

            <MDBox pt={3} pb={3} px={4}>
              <Grid container spacing={4} justifyContent="center">
                <Field name="uid" component="input" type="hidden" />

                {/* NAME */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <Field name="firstName" component={FormField} label={t("First Name")} fullWidth />
                </Grid>

                {/* LAST NAME */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <Field name="lastName" component={FormField} label={t("Last Name")} fullWidth />
                </Grid>

                {/* EMAIL */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <Field name="email" component={FormField} label={t("Email")} fullWidth />
                </Grid>

                {/* PHONE (OPTIONAL) */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <Field
                    name="phoneNumber"
                    component={FormField}
                    label={t("Phone Number")}
                    fullWidth
                  />
                </Grid>

                {/* PROJECTS */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <Field
                    name="projects"
                    component={FormSelectorMulti}
                    values={projectsOptions.values}
                    names={projectsOptions.names}
                    label={t("projects")}
                  />
                </Grid>

                {/* FEATURES */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <MDBox mb={2}>
                    <MDBox fontWeight="bold" fontSize="1.2rem" color="text" mt={5} mb={1}>
                      {t("sections").toUpperCase()}
                    </MDBox>
                    <MDBox fontSize="1rem" color="secondary">
                      {t("projectManagerSharedSectionsDescription")}
                    </MDBox>
                  </MDBox>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <Field
                        name="features.amenities"
                        component={FormCheckbox}
                        label={t("amenities")}
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Field
                        name="features.analytics"
                        component={FormCheckbox}
                        label={t("analytics")}
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Field name="features.chat" component={FormCheckbox} label={t("chat")} />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Field
                        name="features.services"
                        component={FormCheckbox}
                        label={t("services")}
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Field
                        name="features.support"
                        component={FormCheckbox}
                        label={t("support")}
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Field
                        name="features.clients"
                        component={FormCheckbox}
                        label={t("clientReservations")}
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Field
                        name="features.apartments"
                        component={FormCheckbox}
                        label={t("apartments")}
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Field
                        name="features.notifications"
                        component={FormCheckbox}
                        label={t("notifications")}
                      />
                    </Grid>
                    {/* <Grid size={{ xs: 6 }}>
                      <Field
                        name="features.certifications"
                        component={FormCheckbox}
                        label={t("certifications")}
                      />
                    </Grid> */}
                  </Grid>

                  <MDBox mb={2}>
                    <MDBox fontWeight="bold" fontSize="1.2rem" color="text" mt={5} mb={1}>
                      {t("menuSections").toUpperCase()}
                    </MDBox>
                    <MDBox fontSize="1rem" color="secondary">
                      {t("projectManagerExclusiveMenuSectionsDescription")}
                    </MDBox>
                  </MDBox>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <Field
                        name="features.configuration"
                        component={FormCheckbox}
                        label={t("projectManagerMenuAccess")}
                      />
                    </Grid>
                    {isProjectMenuEnabled && (
                      <>
                        <Grid size={{ xs: 6 }}>
                          <Field
                            name="features.settings"
                            component={FormCheckbox}
                            label={t("settings")}
                          />
                        </Grid>
                        {isSettingsEnabled && (
                          <>
                            <Grid size={{ xs: 6 }}>
                              <Field
                                name="features.email"
                                component={FormCheckbox}
                                label={t("email")}
                              />
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                              <Field
                                name="features.customApp"
                                component={FormCheckbox}
                                label={t("Custom Welcome Mobile")}
                              />
                            </Grid>
                            {isAnalyticsEnabled && (
                              <Grid size={{ xs: 6 }}>
                                <Field
                                  name="features.metricsConfig"
                                  component={FormCheckbox}
                                  label={t("metricConfig")}
                                />
                              </Grid>
                            )}
                          </>
                        )}
                        <Grid size={{ xs: 6 }}>
                          <Field
                            name="features.devices"
                            component={FormCheckbox}
                            label={t("devices")}
                          />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Field
                            name="features.occupancy"
                            component={FormCheckbox}
                            label={t("occupancy")}
                          />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Field
                            name="features.cloudApis"
                            component={FormCheckbox}
                            label={t("cloudApis")}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Grid>

                {/* SUBMIT */}
                <Grid size={{ xs: 12 }}>
                  <MDBox display="flex" justifyContent="center" mt={2}>
                    <MDButton
                      type="submit"
                      variant="gradient"
                      color="dark"
                      size="small"
                      //   disabled={isSaving}
                    >
                      {t("Save")}
                      {/* {isSaving ? t("Saving") : t("Save")} */}
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </Grid>
      </form>
    </MDBox>
  );
}

ProjectManagerForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  actionType: PropTypes.string.isRequired,
  projectsOptions: PropTypes.shape({
    values: PropTypes.array,
    names: PropTypes.array,
  }).isRequired,

  actAddProjectManager: PropTypes.func.isRequired,
  actUpdateProjectManager: PropTypes.func.isRequired,
  actNewAlertMessage: PropTypes.func.isRequired,
  onSuccessClose: PropTypes.func,
  projectManagerUid: PropTypes.string,
  isProjectMenuEnabled: PropTypes.bool,
  isSettingsEnabled: PropTypes.bool,
  isAnalyticsEnabled: PropTypes.bool,
};

ProjectManagerForm.defaultProps = {
  onSuccessClose: null,
  projectManagerUid: "",
  isProjectMenuEnabled: false,
  isSettingsEnabled: false,
  isAnalyticsEnabled: false,
};

const mapStateToProps = (state, ownProps) => {
  const { actionType, projectManagerUid } = ownProps;
  const selector = formValueSelector("ProjectManagerForm");
  const isProjectMenuEnabled = !!selector(state, "features.configuration");
  const isSettingsEnabled = !!selector(state, "features.settings");
  const isAnalyticsEnabled = !!selector(state, "features.analytics");
  const projects = Object.values(state.projectsReducer || {});
  const projectManagers = state.projectManagers || {};

  const projectData = projects.map((project) => ({
    uid: project.uid,
    name: project.name,
  }));

  const initial = {
    uid: "",
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    projects: [],
    features: {
      amenities: false,
      analytics: false,
      chat: false,
      services: false,
      support: false,
      clients: false,
      apartments: false,
      notifications: false,
      configuration: false,
      settings: false,
      email: false,
      devices: false,
      occupancy: false,
      cloudApis: false,
      customApp: false,
      metricsConfig: false,
      // certifications: false,
    },
  };

  if (actionType === "edit" && projectManagerUid && projectManagers[projectManagerUid]) {
    const existingManager = projectManagers[projectManagerUid];
    initial.uid = existingManager?.uid || projectManagerUid;
    initial.firstName = existingManager?.firstName || "";
    initial.lastName = existingManager?.lastName || "";
    initial.email = existingManager?.email || "";
    initial.phoneNumber = existingManager?.phoneNumber || "";
    initial.projects = Object.keys(existingManager?.projects || {});
    initial.features = {
      amenities: !!existingManager?.features?.amenities,
      analytics: !!existingManager?.features?.analytics,
      chat: !!existingManager?.features?.chat,
      services: !!existingManager?.features?.services,
      support: !!existingManager?.features?.support,
      clients: !!existingManager?.features?.clients,
      apartments: !!existingManager?.features?.apartments,
      notifications: !!existingManager?.features?.notifications,
      configuration: !!existingManager?.features?.configuration,
      settings: !!existingManager?.features?.settings,
      email: !!existingManager?.features?.email,
      devices: !!existingManager?.features?.devices,
      occupancy: !!existingManager?.features?.occupancy,
      cloudApis: !!existingManager?.features?.cloudApis,
      customApp: !!existingManager?.features?.customApp,
      metricsConfig: !!existingManager?.features?.metricsConfig,
      // certifications: !!existingManager?.features?.certifications,
    };
  }

  return {
    initialValues: initial,
    projectsOptions: {
      values: projectData.map((p) => p.uid),
      names: projectData,
    },
    isProjectMenuEnabled,
    isSettingsEnabled,
    isAnalyticsEnabled,
  };
};

const mapDispatchToProps = (dispatch) => ({
  actNewAlertMessage: (message) => dispatch(newAlertMessage(message)),
  actAddProjectManager: (projectManagerData) =>
    new Promise((resolve, reject) => {
      dispatch(sendAddProjectManager(projectManagerData, resolve, reject));
    }),
  actUpdateProjectManager: (projectManagerData) =>
    new Promise((resolve, reject) => {
      dispatch(sendUpdateProjectManager(projectManagerData, resolve, reject));
    }),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  reduxForm({
    form: "ProjectManagerForm",
    validate,
    enableReinitialize: true,
  })(ProjectManagerForm)
);
